import { toast } from "react-toastify";

import {
  ZERO,
  messages,
  ONE_THOUSAND,
  EMPTY_STRING,
  QUESTION_TYPE_IDS,
  eventBookingStatus,
  BASE64_IMAGE_PREFIX,
  eventBookingConstants,
} from "../../constants";
import {
  deepClone,
  formatTime,
  customToast,
  isArrayWithItems,
  formatEpochToDate,
  isHtmlContentEmpty,
  isDateBeforeMinimumBookingDate,
  isTimeInEpochSecond,
  formatTimeToTimeZone,
} from "../utility";

// *** Event Setup (START)***//

// Returns true if the given epoch date (in seconds) is before the minimum booking lead date
export const isEpochDateBeforeMinimumBookingDate = (epochSeconds) => {
  if (!epochSeconds) return true;

  const date = new Date(epochSeconds * ONE_THOUSAND);
  return isDateBeforeMinimumBookingDate(date);
};

// Utility: Handles event deletion logic.
// - If not edit mode OR event is newly added then remove from formState.
// - If event status is edited replace with status cancel (and mark orderItems as cancel).
// - Otherwise leave unchanged.
export const updateFormStateOnDelete = (
  formState,
  eventId,
  isEditEvent,
  editEvents
) => {
  const currentEvent = formState.find((e) => e.id === eventId);
  if (!currentEvent) return formState;

  // Remove if not edit mode OR if event is newly added in edit mode
  if (!isEditEvent || currentEvent.status === eventBookingStatus.CREATED) {
    return formState.filter((event) => event.id !== eventId);
  }
  // Replace with satus cancel if status is EDITED
  if (
    currentEvent.status === eventBookingStatus.EDITED ||
    currentEvent.status === eventBookingStatus.NOTIFY_ADMIN
  ) {
    const editEvent = editEvents?.events?.find((e) => e.id === eventId);
    if (!editEvent) return formState;

    const deletedEvent = {
      ...editEvent,
      status: eventBookingStatus.CANCEL,
      orderItems: (editEvent.orderItems || []).map((item) => ({
        ...item,
        status: eventBookingStatus.CANCEL,
      })),
    };

    return formState.map((event) =>
      event.id === eventId ? deletedEvent : event
    );
  }

  // Other statuses no change
  return formState;
};

// *** Event Setup (END)***//

// *** Add Recaps (START)*** //

// This function filters the logoData array to only include items
// that have matching eventId and recapId present in the addRecapz array
export const syncLogoDataWithAddRecapz = (logoData, addRecapz) => {
  // Create a Set to store valid (eventId-recapId) pairs from addRecapz
  const validPairs = new Set(
    addRecapz.flatMap((event) =>
      (event.reelDetail || []).map(
        (recapItem) => `${event.eventId}-${recapItem.id}`
      )
    )
  );

  // Filter logoData by checking if each (eventId-recapId) pair exists in validPairs
  return logoData.filter((logo) =>
    validPairs.has(`${logo.eventId}-${logo.recapId}`)
  );
};

// Returns the file name of the logo, user-uploaded files or backend URLs
export const getReelsLogoName = (currentRecap, logoData, currentEvent) => {
  // Check if there's a user-uploaded logo
  const uploadedLogo = logoData.find(
    (item) =>
      item.eventId === currentEvent.eventId && item.recapId === currentRecap.id
  );

  if (uploadedLogo?.name) return uploadedLogo.name;

  // If not, check if currentRecap has backend URL
  if (currentRecap.logoUrl) {
    // return empty string if logoUrl looks like base64 (data URI)
    if (currentRecap.logoUrl.startsWith(BASE64_IMAGE_PREFIX)) {
      return EMPTY_STRING;
    }

    // Extract file name from URL
    const parts = currentRecap.logoUrl.split("/");
    return parts[parts.length - 1];
  }

  return EMPTY_STRING;
};

// generate a unique ID for recapz
export const generateUniqueIdForReels = (editEvents, addRecapz) => {
  // Collect ids from top-level reels coming from backend (Edit event booking case)
  const reelIds = Array.isArray(editEvents?.reels)
    ? editEvents.reels.map((r) => r.id || 0)
    : [];

  // Collect ids from recap details (current)
  const recapIds = Array.isArray(addRecapz)
    ? addRecapz.flatMap((item) =>
        Array.isArray(item.reelDetail)
          ? item.reelDetail.map((d) => d.id || 0)
          : []
      )
    : [];

  // Merge both ids
  const allIds = [...reelIds, ...recapIds];

  // Find the max id number
  const maxId = allIds.length ? Math.max(...allIds) : 0;

  // Return next unique id
  return maxId + 1;
};

// Validates that every recap in the list has reelDetail items
// and each reelDetail contains both `orientation` and `reelMode`.
export const validateRecapzReelDetails = (recaps) => {
  if (!Array.isArray(recaps)) return false;

  return recaps.every((recap) =>
    isArrayWithItems(recap?.reelDetail)
      ? recap.reelDetail.every(
          (detail) => detail?.orientation && detail?.reelMode
        )
      : true
  );
};

// Check if any reelDetail inside addRecapz has an empty description
export const hasEmptyReelDetailDescription = (recaps = []) => {
  return (
    recaps
      // collect all reelDetail arrays into a single array
      ?.flatMap((recap) => recap.reelDetail ?? [])
      // check if any description is empty
      .some((reelItem) => isHtmlContentEmpty(reelItem?.description))
  );
};

// Builds a brief entry object for the recap summary.
// For combined edit reel, it collects event details from formState based on eventIds in reel.
// For single event reel, it uses the provided event object directly.
export const buildRecapBriefPayload = (recap, formState, event = null) => {
  let eventTypes = [];

  const mapEventData = (ev) => ({
    eventName: ev?.eventName,
    eventType: ev?.eventType?.name,
    shotList: ev?.eventType?.description,
  });

  if (recap?.isCombinedEventsReel) {
    // collect all eventIds from reelDetail.eventOrderItemMap
    const orderEventIds = recap?.reelDetail
      ?.flatMap((rd) => rd.eventOrderItemMap?.map((oe) => oe.eventId) || [])
      .filter(Boolean);

    // map them from formState
    eventTypes = formState
      .filter((ev) => orderEventIds.includes(ev.id))
      .map(mapEventData);
  } else {
    // normal single-event recap
    eventTypes = [mapEventData(event)];
  }

  const reelsDetail =
    recap?.reelDetail?.map((reel) => ({
      reelName: reel.reelName,
      reelMode: reel.reelMode,
    })) || [];

  return {
    eventTypes,
    reelsDetail,
    isCombinedEventsReel: recap?.isCombinedEventsReel || false,
  };
};

// *** Add Recaps (END)*** //

// *** Handle Drafts Events (START)*** //

// Enhances each event with formatted eventDate, eventStartTime, sbStartTime,
// Extract orderItems with sbLocation (ShutterB) details.
export const formatDraftEvents = (events = []) => {
  return events.map((event) => {
    const updatedOrderItems = event.orderItems?.map((item) => {
      const shutterB = item.sbLocation || {};
      return {
        ...item,
        firstName: shutterB.firstName,
        lastName: shutterB.lastName,
        averageReview: shutterB.averageReview,
        hourlyRate: shutterB.hourlyRate,
        sbReviews: shutterB.sbReviews,
        sbportfolioGalleries: shutterB?.sbportfolioGalleries,
        userName: shutterB.userName,
        device: shutterB?.device,
        isSelected: true,
      };
    });

    return {
      ...event,
      orderItems: updatedOrderItems,
      eventDate: formatEpochToDate(event?.eventDate, EMPTY_STRING),
      eventStartTime: isTimeInEpochSecond(event?.eventStartTime)
        ? event?.eventLocation?.timezone
          ? formatTimeToTimeZone(
              event?.eventStartTime,
              event?.eventLocation?.timezone
            )
          : formatTime(event.eventStartTime)
        : event?.eventStartTime || EMPTY_STRING,
      sbStartTime: isTimeInEpochSecond(event?.sbStartTime)
        ? event?.eventLocation?.timezone
          ? formatTimeToTimeZone(
              event?.sbStartTime,
              event?.eventLocation?.timezone
            )
          : formatTime(event.sbStartTime)
        : event?.sbStartTime || EMPTY_STRING,
    };
  });
};

// Returns true if the provided eventGroupId matches the draftEventGroupId (Edit Draft Event)
export const isMatchingDraftEventGroup = (eventGroupId, draftEventGroupId) => {
  if (eventGroupId === undefined || draftEventGroupId === undefined) {
    return false;
  }
  return eventGroupId === draftEventGroupId;
};

// Marks the specified shutterB as unselected (isSelected: false) in the specified event
export const markShutterBUnselectedInDraftEvent = (
  formState = [],
  eventId,
  shutterBId
) => {
  if (!Array.isArray(formState)) return [];

  return formState.map((event) => {
    if (event.id === eventId) {
      return {
        ...event,
        orderItems: (event.orderItems || []).map((item) =>
          item.sbUserId === shutterBId ? { ...item, isSelected: false } : item
        ),
      };
    }
    return event;
  });
};
// *** Handle Drafts Events (END)*** //

// *** Cart Summary (START)*** //

// This function filters the orderItems of all events in the formState,
// keeping only the items where isSelected === true,
// and removes the sbLocation field from each orderItem.
// Use this when handling edit draft events.
export const filterSelectedShutterBsInEditDraftEvents = (formState = []) => {
  if (!Array.isArray(formState)) return [];

  return formState.map((event) => ({
    ...event,
    orderItems: (event.orderItems || [])
      .filter((item) => item.isSelected === true)
      .map(({ sbLocation, ...itemWithoutSBLocation }) => itemWithoutSBLocation),
  }));
};

// function to get price from recapTypes array
export const getRecapPrice = (type, recapTypes) => {
  const match = recapTypes.find(
    (r) => r.name.toLowerCase() === type?.toLowerCase()
  );
  return match?.price ?? 0;
};

// Validate Event Booking on Checkout
export const validateEventBooking = (dataToSend, isAddUpdateRecap) => {
  if (!dataToSend?.order?.events || !Array.isArray(dataToSend.order.events)) {
    return false;
  }

  const validateDate = dataToSend.order.events.every(
    (event) => !isEpochDateBeforeMinimumBookingDate(event?.eventDate)
  );

  if (!validateDate && !isAddUpdateRecap) {
    customToast(
      messages.PLEASE_ENSURE_ALL_EVENT_DATES_ARE_ON_OR_AFTER_TOMORROW,
      toast.TYPE.ERROR
    );
    return false;
  }

  return true;
};

// *** Cart Summary (END)*** //

// *** Edit Event Booking (START)*** //

// set status to the array list
export const setStatusEventBooking = (array = [], status, field = "status") =>
  (array || []).map((item) => {
    // If this is an event with orderItems, check their statuses
    // If any orderItems status is NOTIFY_ADMIN then set the event status as NOTIFY_ADMIN
    if (
      item.orderItems &&
      item.orderItems.some(
        (oi) => oi.status === eventBookingStatus.NOTIFY_ADMIN
      )
    ) {
      return {
        ...item,
        [field]: eventBookingStatus.NOTIFY_ADMIN,
      };
    }

    // set the event's status to the value passed in `status`.
    // If any orderItem already has status NOTIFY_ADMIN, leave it unchanged otherwise, overwrite with the new status.
    return {
      ...item,
      [field]:
        item[field] === eventBookingStatus.NOTIFY_ADMIN ? item[field] : status,
    };
  });

// Transform reels array into addRecapz structure for Edit Event Booking on get edit event call
export const transformReelsInEditEvent = (reels = [], allEventId) => {
  if (!Array.isArray(reels) || reels.length === 0) return [];

  return Object.values(
    reels
      .map((reel) => ({
        ...reel,
        eventId: !reel?.isCombinedEventsReel
          ? reel?.eventOrderItemMap?.[0].eventId
          : allEventId, // assign all event default id if eventId is null
      }))
      .reduce((acc, reel) => {
        const key = reel.eventId;
        if (!acc[key]) {
          acc[key] = {
            eventId: key,
            isChecked: true,
            reelDetail: [],
            quantity: 0,
            isCombinedEventsReel: reel.isCombinedEventsReel,
          };
        }

        acc[key].reelDetail.push({
          ...reel.reelDetail, // include all fields from reelDetail
          id: reel.id,
          price: reel.price, // include price from top-level reel
          status: eventBookingStatus.EDITED, // set EDITED status
          eventOrderItemMap: reel.eventOrderItemMap,
        });

        acc[key].quantity += reel.quantity || 0;

        return acc;
      }, {})
  );
};

export const isAddRecapzIdentical = (addRecapz, addRecapzBackup) => {
  if (!Array.isArray(addRecapz) || !Array.isArray(addRecapzBackup))
    return false;
  if (addRecapz.length !== addRecapzBackup.length) return false;

  return addRecapz.every((recap, i) => {
    const backupRecap = addRecapzBackup[i];
    if (!backupRecap) return false;

    //Compare quantity
    if (recap.quantity !== backupRecap.quantity) return false;

    //Compare reelDetail length
    if (
      (recap.reelDetail?.length || 0) !== (backupRecap.reelDetail?.length || 0)
    )
      return false;

    //Compare eventOrderItemMap -> eventId values
    const allMatch = recap.reelDetail.every((detail, j) => {
      const backupDetail = backupRecap.reelDetail[j];
      if (!backupDetail) return false;

      //Compare reelMode
      if (detail.reelMode !== backupDetail.reelMode) return false;

      //Compare orientation
      if (detail.orientation !== backupDetail.orientation) return false;

      const currentIds = (detail.eventOrderItemMap || [])
        .map((m) => m.eventId)
        .sort();
      const backupIds = (backupDetail.eventOrderItemMap || [])
        .map((m) => m.eventId)
        .sort();

      if (currentIds.length !== backupIds.length) return false;

      // compare eventIds one-by-one
      return currentIds.every((id, idx) => id === backupIds[idx]);
    });

    return allMatch;
  });
};

// Merge addRecapz with backup (from edit events recaps) to show on cart summary page
export const mergeRecapzWithBackup = (addRecapz = [], addRecapzBackup = []) => {
  // Convert addRecapz to a Map for quick lookup
  const merged = deepClone(addRecapz);
  const recapMap = new Map(merged.map((item) => [item.eventId, item]));

  addRecapzBackup.forEach((backup) => {
    const existing = recapMap.get(backup.eventId);

    //Instead of flattening, merge eventOrderItemMap per reel
    const mergedReelDetails =
      existing?.reelDetail?.map((reel) => {
        const backupReel = (backup.reelDetail || []).find(
          (b) => b.id === reel.id || b.orderItemId === reel.orderItemId
        );

        const updatedMap = reel.eventOrderItemMap || [];
        const dbMap = backupReel?.eventOrderItemMap || [];

        const mergedMap = updatedMap.map((updatedItem) => {
          const dbItem = dbMap.find((d) => d.eventId === updatedItem.eventId);
          return {
            ...(dbItem || {}),
            ...updatedItem,
            isActive: updatedItem.isActive ?? true,
          };
        });

        const missingFromUpdated = dbMap
          .filter(
            (dbItem) => !updatedMap.some((u) => u.eventId === dbItem.eventId)
          )
          .map((dbItem) => ({
            ...dbItem,
            isActive: false,
          }));

        return {
          ...reel,
          eventOrderItemMap: [...mergedMap, ...missingFromUpdated],
        };
      }) || [];

    // NEW — id-based lookup helper, replaces every reelName-based .find() below
    const findMergedMap = (targetId) =>
      mergedReelDetails.find(
        (r) => r.id === targetId || r.orderItemId === targetId
      )?.eventOrderItemMap;

    if (!existing) {
      // EventId not found in addRecapz mark all the recaps as CANCEL
      merged.push({
        ...backup,
        reelDetail: backup.reelDetail.map((reel) => ({
          ...reel,
          status: eventBookingStatus.CANCEL,
          eventOrderItemMap:
            findMergedMap(reel.id) || reel.eventOrderItemMap || [],
        })),
        isQuantityIncrease: false, // no addRecapz to compare, default false
      });
    } else {
      // Compare quantities
      existing.isQuantityIncrease =
        (existing.quantity ?? 0) > (backup.quantity ?? 0);

      // backupReels is reels from the get edit event (backup)
      const backupReels = backup.reelDetail ?? [];

      // existingReels is reels that is currently avaiable in add recap page (Event booking form)
      const existingReels = existing.reelDetail ?? [];

      if (backupReels.length > existingReels.length) {
        const existingIds = new Set(existingReels.map((r) => r.id));

        existingReels.forEach((reel) => {
          const backupMatch = backupReels.find((b) => b.id === reel.id);
          if (backupMatch && reel.status !== eventBookingStatus.CANCEL) {
            reel.status = backupMatch.status;
          }
          reel.eventOrderItemMap =
            findMergedMap(reel.id) || reel.eventOrderItemMap; // was: reelName lookup
        });

        backupReels
          .filter((b) => !existingIds.has(b.id))
          .forEach((backupReel) => {
            existing.reelDetail.push({
              ...backupReel,
              status: eventBookingStatus.CANCEL,
              eventOrderItemMap:
                findMergedMap(backupReel.id) || backupReel.eventOrderItemMap, // was: reelName lookup
            });
          });
      } else if (backupReels.length <= existingReels.length) {
        backupReels.forEach((backupReel) => {
          const current = existing.reelDetail.find(
            (r) => r.id === backupReel.id
          );
          if (current) {
            if (current.status !== eventBookingStatus.CANCEL) {
              current.status = backupReel.status;
            }
            current.eventOrderItemMap =
              findMergedMap(backupReel.id) || current.eventOrderItemMap; // was: reelName lookup
          }
        });
      }
    }
  });

  return merged;
};

// set orderitems status to notify_admin if the event's is notify_admin
export const syncNotifyAdminOrderItems = (
  filteredFormState,
  editEvents = []
) => {
  if (!Array.isArray(filteredFormState) || filteredFormState.length === 0) {
    return filteredFormState;
  }

  return filteredFormState.map((event) => {
    // Only touch events already marked notify_admin in filteredFormState
    if (event.status !== eventBookingStatus.NOTIFY_ADMIN) {
      return event;
    }

    // find matching event in editEvents (only match by id)
    const matchingEditEvent = editEvents?.find((e) => e.id === event.id);

    if (!matchingEditEvent) {
      return event;
    }

    return {
      ...event,
      orderItems: matchingEditEvent.orderItems
        ? matchingEditEvent.orderItems.map((item) => ({
            ...item,
            status: eventBookingStatus.NOTIFY_ADMIN,
          }))
        : [],
    };
  });
};

// Mark isDurationIncrease flag for orderItems if event duration is increased
export const markDurationIncreaseFlag = (events = []) => {
  if (!isArrayWithItems(events)) {
    return events;
  }

  return events.map((event) => {
    if (event.status === eventBookingStatus.CANCEL) {
      return event; // skip cancelled or notify-admin events
    }

    const isIncrease = (event.duration ?? 0) > (event.oldDuration ?? 0);
    return {
      ...event,
      orderItems:
        event.orderItems?.map((item) => {
          if (item.status === eventBookingStatus.CREATED) {
            return {
              ...item,
              isDurationIncrease: true,
            };
          }
          if (item.status === eventBookingStatus.CANCEL) {
            return {
              ...item,
              isDurationIncrease: false,
            };
          }
          return {
            ...item,
            isDurationIncrease: isIncrease,
          };
        }) ?? [],
    };
  });
};

// *** Edit Event booking (END)*** //

// *** Shutterb Selection Page (START)*** //

// Check if current event has at least one valid order item
export const hasValidOrderItems = (formState, currentIndex, targetEventId) => {
  if (!Array.isArray(formState) || formState.length === 0) return false;

  const targetIndex = formState.findIndex((e) => e.id === targetEventId);
  // If moving backward skip validation
  if (targetIndex !== -1 && targetIndex < currentIndex) {
    return true;
  }

  const currentEvent = formState[currentIndex];
  // In case of discard currentEvent is not found so no need to validate
  if (!currentEvent) return true;

  // If event status is notify_admin consider it valid
  if (currentEvent.status === eventBookingStatus.NOTIFY_ADMIN) {
    return true;
  }

  if (currentEvent.status === eventBookingStatus.NOTIFY_ADMIN) {
    const hasValidNotifyAdminItem = (currentEvent.orderItems || []).some(
      (item) => item.status === eventBookingStatus.NOTIFY_ADMIN
    );

    if (hasValidNotifyAdminItem) return true;
  }

  // If event is save as draft consider it valid
  if (currentEvent.isSaveAsDraft) {
    return true;
  }

  // check all order items status is not cancel or notify admin
  const orderItems = currentEvent.orderItems || [];
  const isValid = orderItems.some(
    (item) =>
      item.status !== eventBookingStatus.CANCEL &&
      item.status !== eventBookingStatus.NOTIFY_ADMIN
  );
  return isValid;
};

// Add status "created" or "edited" to Available orderItems in shutterb selection page
export const addStatusToOrderItems = (events = []) => {
  return (events || []).map((event) => ({
    ...event,
    items: (event.items || []).map((item) => ({
      ...item,
      // if already assigned set status to "edited"
      // otherwise set status to "created"
      status: item.isAlreadyAssigned
        ? eventBookingStatus.EDITED
        : eventBookingStatus.CREATED,
    })),
  }));
};

// *** Shutterb Selection Page (END)*** //

// Common Eventbooking Utility

// Filter out items whose status is in the excludedStatuses list
export const filterByStatus = (array = [], excludedStatuses = []) => {
  if (!Array.isArray(array) || !Array.isArray(excludedStatuses)) return [];
  return (array || []).filter(
    (item) => !excludedStatuses.includes(item?.status)
  );
};

// create reels structure for sending to backend
export const normalizeAddRecapz = (addRecapz, reels = []) => {
  const normalized = [];

  addRecapz.forEach((recap) => {
    if (!Array.isArray(recap.reelDetail)) return;

    recap.reelDetail.forEach((recapDetail) => {
      const matchedReel = reels?.find((reel) => reel?.id === recapDetail.id);

      // Case 1: reel exists & status is not cancel
      if (matchedReel && recapDetail.status !== eventBookingStatus.CANCEL) {
        normalized.push({
          ...matchedReel,
          price: recapDetail.price,
          quantity:
            matchedReel.quantity || eventBookingConstants.REEL.DEFAULT_QUANTITY,
          status: recapDetail.status || eventBookingStatus.EDITED,
          eventOrderItemMap: [...recapDetail.eventOrderItemMap],
          reelDetail: {
            ...recapDetail,
            id: matchedReel.reelDetail.id,
            orderId: matchedReel.reelDetail.orderId,
            orderItemId: matchedReel.reelDetail.orderItemId,
            musicFilePath: matchedReel.reelDetail.musicFilePath,
            logoUrl: recapDetail.logoUrl || EMPTY_STRING,
            reelQuestionAnswer: {
              ...matchedReel.reelDetail.reelQuestionAnswer,
              ...recapDetail.reelQuestionAnswer,
            },
          },
        });
        return;
      }

      // Case 2: reel exists & recapDetail is cancel
      if (matchedReel && recapDetail.status === eventBookingStatus.CANCEL) {
        normalized.push({
          ...matchedReel,
          eventId: recap.eventId,
          status: eventBookingStatus.CANCEL,
          eventOrderItemMap: recapDetail.eventOrderItemMap.map((item) => ({
            ...item,
            isActive: false, // true if selected, false otherwise
          })),
          reelDetail: {
            ...recapDetail,
            id: matchedReel.reelDetail.id,
          },
        });
        return;
      }

      // Case 3: reel not found create default object
      if (!matchedReel) {
        normalized.push(
          createDefaultReelObject(
            recap.eventId,
            recapDetail,
            recap.isCombinedEventsReel
          )
        );
      }
    });
  });

  return normalized;
};

// Generate reels for checkout
export const generateReels = (myReels) => {
  const reels = [];

  if (!Array.isArray(myReels)) return reels;

  myReels.forEach((event) => {
    if (Array.isArray(event.reelDetail)) {
      event.reelDetail.forEach((reelItem) => {
        reels.push(
          createDefaultReelObject(
            event.eventId,
            reelItem,
            event.isCombinedEventsReel
          )
        );
      });
    }
  });

  return reels;
};

// Create a default reel object structure in case of new reel added
export const createDefaultReelObject = (
  eventId,
  reelDetail = {},
  isCombinedEventsReel = false
) => {
  return {
    eventId: eventId?.toString() || EMPTY_STRING,
    orderItemTypeId: eventBookingConstants.ORDER_ITEM_TYPE_ID.REEL,
    sbUserId: null,
    price: reelDetail.price || ZERO,
    quantity: eventBookingConstants.REEL.DEFAULT_QUANTITY,
    status: reelDetail.status || eventBookingStatus.CREATED,
    eventOrderItemMap: reelDetail?.eventOrderItemMap || [],
    isCombinedEventsReel,
    reelDetail: {
      reelName: reelDetail.reelName || EMPTY_STRING,
      logoUrl: reelDetail.logoUrl || EMPTY_STRING,
      musicFilePath: reelDetail.musicFilePath || null,
      music: reelDetail.music || EMPTY_STRING,
      orientation: reelDetail.orientation || EMPTY_STRING,
      reelType: reelDetail.reelType || EMPTY_STRING,
      duration: Number(reelDetail.duration) || ZERO,
      description: reelDetail.description || EMPTY_STRING,
      reelMode: reelDetail.reelMode,
      reelQuestionAnswer: {
        questionTypeId: QUESTION_TYPE_IDS.ADD_RECAP_PURPOSE,
        answer: reelDetail.reelQuestionAnswer?.answer || EMPTY_STRING,
      },
    },
    tipPrice: ZERO,
    isActive: false,
    paymentStatus: false,
  };
};
